<template>
  <div class="prompt-mention-editor-wrap">
    <div
      ref="editorRef"
      class="prompt-mention-editor"
      contenteditable="true"
      role="textbox"
      aria-multiline="true"
      :data-placeholder="placeholder"
      @beforeinput="handleBeforeInput"
      @keydown="handleKeydown"
      @input="handleInput"
      @blur="handleBlur"
      @paste="handlePaste"
      @compositionstart="isComposing = true"
      @compositionend="handleCompositionEnd"
    ></div>

    <Teleport to="body">
      <div
        v-if="previewVisible && previewCharacter"
        class="mention-preview-popup"
        :style="previewStyle"
        @mouseenter="cancelHidePreview"
        @mouseleave="scheduleHidePreview"
      >
        <img
          v-if="previewImageUrl"
          :src="previewImageUrl"
          :alt="previewCharacter.name"
          class="mention-preview-img"
        />
        <div v-else class="mention-preview-placeholder">
          {{ previewCharacter.name?.[0] ?? '?' }}
        </div>
        <span class="mention-preview-name">{{ previewCharacter.name }}</span>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
  import type { CharacterItem } from '@/api/character';

  interface MentionSegment {
    type: 'text' | 'mention';
    text?: string;
    name?: string;
    avatarUrl?: string;
  }

  const props = withDefaults(
    defineProps<{
      modelValue: string;
      characters: CharacterItem[];
      placeholder?: string;
      maxlength?: number;
    }>(),
    {
      placeholder: '',
      maxlength: 800
    }
  );

  const emit = defineEmits<{
    'update:modelValue': [value: string];
    /** 输入 @ 时触发，供外层打开角色选择弹层 */
    'mention-trigger': [];
  }>();

  const editorRef = ref<HTMLDivElement | null>(null);
  const isComposing = ref(false);
  /** 失焦前保存在编辑器内的纯文本光标/选区，用于 @ 插入 */
  const savedCaret = ref<{ start: number; end: number } | null>(null);
  /** 点击「选择角色」后锁定，避免弹层/重渲染把光标写成 0 */
  const caretLocked = ref(false);

  const previewVisible = ref(false);
  const previewCharacter = ref<CharacterItem | null>(null);
  const previewStyle = ref<Record<string, string>>({});
  let hidePreviewTimer: ReturnType<typeof setTimeout> | null = null;

  const characterByName = computed(() => {
    const map = new Map<string, CharacterItem>();
    props.characters.forEach((character) => {
      if (character.name) map.set(character.name, character);
    });
    return map;
  });

  const previewImageUrl = computed(() => {
    const character = previewCharacter.value;
    if (!character) return '';
    return character.avatarUrl || character.generatedUrl || character.refImageUrl || '';
  });

  const clampText = (text: string) => {
    if (!props.maxlength || text.length <= props.maxlength) return text;
    return text.slice(0, props.maxlength);
  };

  const ZWSP = '\u200B';

  const isSpacerTextNode = (node: Node | null): node is Text =>
    !!node &&
    node.nodeType === Node.TEXT_NODE &&
    (node.textContent === ZWSP || node.textContent === '');

  const stripZwsp = (text: string) => text.replaceAll(ZWSP, '');

  const textPlainLength = (text: string) => stripZwsp(text).length;

  /** iOS：contenteditable=false 的 chip 后必须有文本节点，否则视觉光标会漂到错误位置 */
  const ensureTextNodeAfter = (chip: HTMLElement) => {
    const next = chip.nextSibling;
    if (next?.nodeType === Node.TEXT_NODE) return next as Text;
    const spacer = document.createTextNode(ZWSP);
    chip.parentNode?.insertBefore(spacer, next);
    return spacer;
  };

  const getNodePlainLength = (node: Node): number => {
    if (node.nodeType === Node.TEXT_NODE) return textPlainLength(node.textContent ?? '');
    if (node instanceof HTMLElement && node.classList.contains('mention-chip')) {
      return 1 + (node.dataset.name?.length ?? 0);
    }
    let len = 0;
    node.childNodes.forEach((child) => {
      len += getNodePlainLength(child);
    });
    return len;
  };

  const getPlainOffset = (container: Node, offset: number): number => {
    const editor = editorRef.value;
    if (!editor) return 0;

    let plain = 0;
    const visit = (node: Node): boolean => {
      if (node === container && node.nodeType === Node.TEXT_NODE) {
        const raw = node.textContent ?? '';
        // 把 DOM 偏移映射到去掉零宽字符后的纯文本偏移
        let mapped = 0;
        for (let i = 0; i < Math.min(offset, raw.length); i += 1) {
          if (raw[i] !== ZWSP) mapped += 1;
        }
        plain += mapped;
        return true;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        plain += textPlainLength(node.textContent ?? '');
        return false;
      }

      if (node instanceof HTMLElement && node.classList.contains('mention-chip')) {
        const mentionLen = 1 + (node.dataset.name?.length ?? 0);
        if (node === container || node.contains(container)) {
          plain += mentionLen;
          return true;
        }
        plain += mentionLen;
        return false;
      }

      if (node === container && node.nodeType === Node.ELEMENT_NODE) {
        const children = [...node.childNodes];
        for (let i = 0; i < offset && i < children.length; i += 1) {
          plain += getNodePlainLength(children[i]);
        }
        return true;
      }

      for (const child of node.childNodes) {
        if (visit(child)) return true;
      }
      return false;
    };

    visit(editor);
    return plain;
  };

  const getSelectionPlainOffsets = (): { start: number; end: number } | null => {
    const editor = editorRef.value;
    if (!editor) return null;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return null;

    const start = getPlainOffset(range.startContainer, range.startOffset);
    const end = getPlainOffset(range.endContainer, range.endOffset);
    return { start: Math.min(start, end), end: Math.max(start, end) };
  };

  const saveCaretFromSelection = () => {
    if (caretLocked.value) return;
    const offsets = getSelectionPlainOffsets();
    if (offsets) savedCaret.value = offsets;
  };

  /** 点击 @ 时调用：保存并锁定当前光标，直到插入完成 */
  const lockCaretFromSelection = () => {
    const offsets = getSelectionPlainOffsets();
    if (offsets) {
      savedCaret.value = offsets;
    } else if (!savedCaret.value) {
      savedCaret.value = {
        start: props.modelValue.length,
        end: props.modelValue.length
      };
    }
    caretLocked.value = true;
  };

  const unlockCaret = () => {
    caretLocked.value = false;
  };

  /** 防止 beforeinput + keydown 重复触发弹层 */
  let mentionTriggerGuard = false;

  /**
   * 输入 @ 后：保留可见的 @，将选区锁定在该字符上，
   * 选中角色时用标签替换这个 @。
   */
  const triggerMentionPickerAfterAt = () => {
    if (mentionTriggerGuard) return;
    mentionTriggerGuard = true;

    requestAnimationFrame(() => {
      const offsets = getSelectionPlainOffsets();
      const text = serializeEditor();
      if (offsets) {
        const atEnd = offsets.start;
        const atStart = Math.max(0, atEnd - 1);
        if (text.slice(atStart, atEnd) === '@') {
          savedCaret.value = { start: atStart, end: atEnd };
        } else {
          savedCaret.value = offsets;
        }
      } else if (!savedCaret.value) {
        savedCaret.value = { start: text.length, end: text.length };
      }
      caretLocked.value = true;
      syncModelValue(text);
      emit('mention-trigger');
      mentionTriggerGuard = false;
    });
  };

  /** 输入 @：不拦截字符，落地后再弹层选角色 */
  const handleBeforeInput = (event: Event) => {
    if (isComposing.value) return;
    const inputEvent = event as InputEvent;
    if (inputEvent.inputType !== 'insertText') return;
    if (inputEvent.data !== '@') return;
    triggerMentionPickerAfterAt();
  };

  /** 光标紧跟角色标签（或仅隔着零宽占位符）时，退格一次直接删掉标签 */
  const tryDeleteMentionChipByBackspace = (event: KeyboardEvent): boolean => {
    const editor = editorRef.value;
    if (!editor) return false;

    const selection = window.getSelection();
    if (!selection || !selection.isCollapsed || selection.rangeCount === 0) return false;

    const range = selection.getRangeAt(0);
    if (!editor.contains(range.startContainer)) return false;

    const removeChip = (chip: HTMLElement) => {
      const spacer = chip.nextSibling;
      chip.remove();
      if (isSpacerTextNode(spacer)) spacer.remove();
      handleInput();
    };

    const container = range.startContainer;
    const offset = range.startOffset;

    if (container.nodeType === Node.TEXT_NODE) {
      const text = container.textContent ?? '';
      const beforeCursor = text.slice(0, offset);
      if (textPlainLength(beforeCursor) > 0) return false;

      const prev = container.previousSibling;
      if (prev instanceof HTMLElement && prev.classList.contains('mention-chip')) {
        event.preventDefault();
        removeChip(prev);
        return true;
      }
      return false;
    }

    if (container === editor || container.nodeType === Node.ELEMENT_NODE) {
      const child = container.childNodes[offset - 1];
      if (child instanceof HTMLElement && child.classList.contains('mention-chip')) {
        event.preventDefault();
        removeChip(child);
        return true;
      }
      if (isSpacerTextNode(child)) {
        const chip = child.previousSibling;
        if (chip instanceof HTMLElement && chip.classList.contains('mention-chip')) {
          event.preventDefault();
          removeChip(chip);
          return true;
        }
      }
    }

    return false;
  };

  const handleKeydown = (event: KeyboardEvent) => {
    if (isComposing.value) return;

    if (event.key === 'Backspace') {
      if (tryDeleteMentionChipByBackspace(event)) return;
    }

    if (event.key !== '@') return;
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    triggerMentionPickerAfterAt();
  };

  const applyCollapsedRange = (targetNode: Node, targetOffset: number) => {
    const range = document.createRange();
    range.setStart(targetNode, targetOffset);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const placeCursorAtPlainOffset = (plainOffset: number) => {
    const editor = editorRef.value;
    if (!editor) return;

    editor.focus();

    let remaining = Math.max(0, plainOffset);
    let targetNode: Node = editor;
    let targetOffset = 0;
    let found = false;

    const visit = (node: Node): boolean => {
      if (node.nodeType === Node.TEXT_NODE) {
        const raw = node.textContent ?? '';
        const len = textPlainLength(raw);
        if (remaining <= len) {
          // 纯文本偏移 -> 含 ZWSP 的 DOM 偏移
          let domOffset = 0;
          let seen = 0;
          while (domOffset < raw.length && seen < remaining) {
            if (raw[domOffset] !== ZWSP) seen += 1;
            domOffset += 1;
          }
          targetNode = node;
          targetOffset = domOffset;
          found = true;
          return true;
        }
        remaining -= len;
        return false;
      }

      if (node instanceof HTMLElement && node.classList.contains('mention-chip')) {
        const mentionLen = 1 + (node.dataset.name?.length ?? 0);
        if (remaining <= mentionLen) {
          // 落在 chip 上/后：放到后续文本节点里，避免 iOS 在元素边界画错光标
          const textNode = ensureTextNodeAfter(node);
          targetNode = textNode;
          targetOffset = isSpacerTextNode(textNode) ? (textNode.textContent?.length ?? 0) : 0;
          found = true;
          return true;
        }
        remaining -= mentionLen;
        return false;
      }

      for (const child of node.childNodes) {
        if (visit(child)) return true;
      }
      return false;
    };

    visit(editor);

    if (!found) {
      // 末尾：保证有可落点的文本节点
      const last = editor.lastChild;
      if (last instanceof HTMLElement && last.classList.contains('mention-chip')) {
        targetNode = ensureTextNodeAfter(last);
        targetOffset = targetNode.textContent?.length ?? 0;
      } else if (last?.nodeType === Node.TEXT_NODE) {
        targetNode = last;
        targetOffset = last.textContent?.length ?? 0;
      } else {
        const spacer = document.createTextNode(ZWSP);
        editor.appendChild(spacer);
        targetNode = spacer;
        targetOffset = 1;
      }
    }

    applyCollapsedRange(targetNode, targetOffset);
    // iOS 首次布局后偶发画错，下一帧再设一次
    requestAnimationFrame(() => {
      if (document.activeElement === editor) {
        applyCollapsedRange(targetNode, targetOffset);
      }
    });
  };

  const cancelHidePreview = () => {
    if (hidePreviewTimer) {
      clearTimeout(hidePreviewTimer);
      hidePreviewTimer = null;
    }
  };

  const hidePreview = () => {
    cancelHidePreview();
    previewVisible.value = false;
    previewCharacter.value = null;
  };

  const scheduleHidePreview = () => {
    cancelHidePreview();
    hidePreviewTimer = setTimeout(() => {
      hidePreview();
    }, 120);
  };

  const showPreviewForChip = (chip: HTMLElement, name: string) => {
    const character = characterByName.value.get(name);
    if (!character) return;

    cancelHidePreview();
    const rect = chip.getBoundingClientRect();
    const popupSize = 130;
    const gap = 8;
    let left = rect.left + rect.width / 2 - popupSize / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popupSize - 8));
    let top = rect.bottom + gap;
    if (top + popupSize > window.innerHeight - 8) {
      top = Math.max(8, rect.top - popupSize - gap);
    }

    previewCharacter.value = character;
    previewStyle.value = {
      left: `${left}px`,
      top: `${top}px`
    };
    previewVisible.value = true;
  };

  const parseDescription = (text: string): MentionSegment[] => {
    const names = [...characterByName.value.keys()].sort((a, b) => b.length - a.length);
    const segments: MentionSegment[] = [];
    let buffer = '';

    const flushText = () => {
      if (!buffer) return;
      segments.push({ type: 'text', text: buffer });
      buffer = '';
    };

    for (let i = 0; i < text.length; i += 1) {
      if (text[i] === '@') {
        const rest = text.slice(i + 1);
        const matched = names.find((name) => rest.startsWith(name));
        if (matched) {
          flushText();
          const character = characterByName.value.get(matched);
          segments.push({
            type: 'mention',
            name: matched,
            avatarUrl: character?.avatarUrl ?? ''
          });
          i += matched.length;
          continue;
        }
      }
      buffer += text[i];
    }

    flushText();
    return segments;
  };

  const createMentionChip = (name: string, avatarUrl: string) => {
    const chip = document.createElement('span');
    chip.className = 'mention-chip';
    chip.contentEditable = 'false';
    chip.dataset.name = name;

    const label = document.createElement('span');
    label.className = 'mention-chip-label';
    label.textContent = `@${name}`;
    chip.appendChild(label);

    if (avatarUrl) {
      const img = document.createElement('img');
      img.className = 'mention-chip-avatar';
      img.src = avatarUrl;
      img.alt = name;
      chip.appendChild(img);
    }

    chip.addEventListener('mouseenter', () => showPreviewForChip(chip, name));
    chip.addEventListener('mouseleave', scheduleHidePreview);

    return chip;
  };

  const serializeEditor = () => {
    const editor = editorRef.value;
    if (!editor) return '';

    let text = '';
    editor.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += stripZwsp(node.textContent ?? '');
        return;
      }

      if (!(node instanceof HTMLElement)) return;

      if (node.classList.contains('mention-chip')) {
        text += `@${node.dataset.name ?? ''}`;
        return;
      }

      text += stripZwsp(node.textContent ?? '');
    });

    return text;
  };

  /** 当前已插入的角色标签名（按出现顺序，可能重复） */
  const getMentionedNames = (): string[] => {
    const editor = editorRef.value;
    if (editor) {
      const chipNames = [...editor.querySelectorAll('.mention-chip')]
        .map((node) => (node instanceof HTMLElement ? (node.dataset.name?.trim() ?? '') : ''))
        .filter(Boolean);
      if (chipNames.length) return chipNames;
    }

    const names = [...characterByName.value.keys()].sort((a, b) => b.length - a.length);
    const mentioned: string[] = [];
    const text = props.modelValue;
    for (let i = 0; i < text.length; i += 1) {
      if (text[i] !== '@') continue;
      const rest = text.slice(i + 1);
      const matched = names.find((name) => rest.startsWith(name));
      if (!matched) continue;
      mentioned.push(matched);
      i += matched.length;
    }
    return mentioned;
  };

  const renderFromPlainText = (text: string) => {
    const editor = editorRef.value;
    if (!editor) return;

    const segments = parseDescription(text);
    editor.innerHTML = '';

    segments.forEach((segment, index) => {
      if (segment.type === 'text' && segment.text) {
        editor.appendChild(document.createTextNode(segment.text));
        return;
      }

      if (segment.type === 'mention' && segment.name) {
        const chip = createMentionChip(segment.name, segment.avatarUrl ?? '');
        editor.appendChild(chip);
        // chip 后若没有真实文本，插入零宽字符，供 iOS 正确绘制光标
        const next = segments[index + 1];
        if (!next || next.type !== 'text' || !next.text) {
          editor.appendChild(document.createTextNode(ZWSP));
        }
      }
    });
  };

  const placeCursorAtEnd = () => {
    const editor = editorRef.value;
    if (!editor) return;

    const last = editor.lastChild;
    if (last instanceof HTMLElement && last.classList.contains('mention-chip')) {
      const textNode = ensureTextNodeAfter(last);
      editor.focus();
      applyCollapsedRange(textNode, textNode.textContent?.length ?? 0);
      return;
    }

    if (last?.nodeType === Node.TEXT_NODE) {
      editor.focus();
      applyCollapsedRange(last, last.textContent?.length ?? 0);
      return;
    }

    editor.focus();
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const syncModelValue = (nextValue: string) => {
    const normalized = clampText(nextValue);
    if (normalized !== props.modelValue) {
      emit('update:modelValue', normalized);
    }
    return normalized;
  };

  const handleInput = () => {
    if (isComposing.value) return;
    let text = serializeEditor();
    if (props.maxlength && text.length > props.maxlength) {
      text = text.slice(0, props.maxlength);
      renderFromPlainText(text);
      placeCursorAtEnd();
    }
    syncModelValue(text);
  };

  const handleCompositionEnd = () => {
    isComposing.value = false;
    handleInput();
  };

  const handleBlur = () => {
    if (!caretLocked.value) saveCaretFromSelection();
    const normalized = syncModelValue(serializeEditor());
    renderFromPlainText(normalized);
  };

  const handlePaste = (event: ClipboardEvent) => {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text/plain') ?? '';
    const current = serializeEditor();
    const offsets = getSelectionPlainOffsets() ??
      savedCaret.value ?? {
        start: current.length,
        end: current.length
      };
    const start = Math.max(0, Math.min(offsets.start, offsets.end, current.length));
    const end = Math.max(start, Math.min(Math.max(offsets.start, offsets.end), current.length));

    const before = current.slice(0, start);
    const after = current.slice(end);
    const available = Math.max(
      0,
      (props.maxlength || Number.POSITIVE_INFINITY) - before.length - after.length
    );
    const insertion = pasted.slice(0, available);
    const next = before + insertion + after;
    const cursorAfter = before.length + insertion.length;

    renderFromPlainText(next);
    syncModelValue(next);
    savedCaret.value = { start: cursorAfter, end: cursorAfter };
    void nextTick(() => placeCursorAtPlainOffset(cursorAfter));
  };

  const handleSelectionChange = () => {
    if (caretLocked.value) return;
    if (document.activeElement !== editorRef.value) return;
    saveCaretFromSelection();
  };

  const appendMention = async (character: CharacterItem) => {
    // 以编辑器 DOM 为准，避免 props.modelValue 尚未同步时覆盖已有角色标签
    const current = serializeEditor() || props.modelValue;
    const mention = `@${character.name}`;
    const caret = savedCaret.value;
    const start = caret?.start ?? current.length;
    const end = caret?.end ?? current.length;

    const before = current.slice(0, start);
    const after = current.slice(end);
    // 不自动补空格，避免退格要按两次才能删掉角色标签
    const insertion = mention;

    if (props.maxlength && before.length + mention.length > props.maxlength) {
      unlockCaret();
      return;
    }

    const next = clampText(before + insertion + after);
    const cursorAfter = Math.min(before.length + insertion.length, next.length);

    syncModelValue(next);
    renderFromPlainText(next);
    savedCaret.value = { start: cursorAfter, end: cursorAfter };
    unlockCaret();
    await nextTick();
    placeCursorAtPlainOffset(cursorAfter);
  };

  watch(
    () => props.modelValue,
    (value) => {
      if (document.activeElement === editorRef.value) return;
      if (serializeEditor() !== value) renderFromPlainText(value);
    }
  );

  watch(
    () => props.characters,
    async () => {
      const caret = savedCaret.value;
      // 角色列表刷新时保留编辑器现有内容，避免尚未同步的 modelValue 冲掉已添加的 @角色
      const text = serializeEditor() || props.modelValue;
      if (text !== props.modelValue) syncModelValue(text);
      renderFromPlainText(text);
      // 仅在编辑器已有焦点时恢复光标。
      // caretLocked（点 @ 打开弹层）时不要 focus，否则 iOS 会再次弹出键盘。
      if (caret && document.activeElement === editorRef.value) {
        await nextTick();
        placeCursorAtPlainOffset(caret.start);
      }
    },
    { deep: true }
  );

  onMounted(() => {
    renderFromPlainText(props.modelValue);
    document.addEventListener('selectionchange', handleSelectionChange);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('selectionchange', handleSelectionChange);
    cancelHidePreview();
  });

  const blurEditor = () => {
    editorRef.value?.blur();
  };

  defineExpose({
    appendMention,
    getMentionedNames,
    lockCaretFromSelection,
    unlockCaret,
    saveCaretFromSelection,
    blurEditor
  });
</script>

<style scoped>
  .prompt-mention-editor-wrap {
    height: auto;
    max-height: none;
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .prompt-mention-editor {
    height: 0;
    flex: 1 1 0;
    width: 100%;
    min-height: 0;
    max-height: 100%;
    box-sizing: border-box;
    padding: 0;
    border: 0;
    outline: 0;
    overflow-y: auto;
    overflow-x: hidden;
    background: transparent;
    color: #1d1a2c;
    font-family: inherit;
    /* iOS Safari 在字号 < 16px 的输入区域获焦时会自动放大页面 */
    font-size: 16px;
    line-height: 1.7;
    white-space: pre-wrap;
    word-break: break-word;
    overscroll-behavior: contain;
    scrollbar-color: rgba(96, 51, 225, 0.35) transparent;
    scrollbar-width: thin;
  }

  .prompt-mention-editor::-webkit-scrollbar {
    width: 4px;
  }

  .prompt-mention-editor::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background: rgba(96, 51, 225, 0.35);
  }

  .prompt-mention-editor:empty::before {
    content: attr(data-placeholder);
    color: #b5afc3;
    pointer-events: none;
  }

  .prompt-mention-editor :deep(.mention-chip) {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 1px 6px 1px 8px;
    margin: 0 1px;
    border-radius: 8px;
    background: rgba(96, 51, 225, 0.05);
    vertical-align: baseline;
    line-height: 1.5;
    cursor: pointer;
  }

  .prompt-mention-editor :deep(.mention-chip-label) {
    color: #6033e1;
    font-weight: 700;
  }

  .prompt-mention-editor :deep(.mention-chip-avatar) {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    object-fit: contain;
    flex-shrink: 0;
  }
</style>

<style>
  .mention-preview-popup {
    position: fixed;
    z-index: 4000;
    width: 130px;
    height: 130px;
    overflow: hidden;
    border-radius: 12px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(29, 26, 44, 0.16);
  }

  .mention-preview-img,
  .mention-preview-placeholder {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #f6f4fb;
  }

  .mention-preview-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6033e1;
    font-size: 36px;
    font-weight: 700;
  }

  .mention-preview-name {
    position: absolute;
    left: 10px;
    bottom: 8px;
    max-width: calc(100% - 20px);
    overflow: hidden;
    color: #1d1a2c;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.9);
  }
</style>
